import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Search, Plus, Trash2, Edit2, Check, X, Database, Package } from "lucide-react";
import { useCatalog } from "../hooks/useCatalog";
import { useSupplies } from "../hooks/useSupplies";
import { bomService } from "../services/bomService";
import { pb } from "../services/pocketbase";

// Os helpers de extração de tags foram movidos e pré-processados na inicialização do CatalogContext.js

export default function BomSubTab() {
  const { catalog, loading: loadingCatalog, findItem } = useCatalog();
  const { supplies, isLoading: isLoadingSupplies } = useSupplies();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSku, setSelectedSku] = useState(null);
  
  // Recipe State
  const [recipeLines, setRecipeLines] = useState([]);
  const [isLoadingRecipe, setIsLoadingRecipe] = useState(false);

  // Form add State
  const [selectedSupplyId, setSelectedSupplyId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados dos Modelos (Templates)
  const [templates, setTemplates] = useState({});
  const [templatesRecord, setTemplatesRecord] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [selectedTemplateName, setSelectedTemplateName] = useState("");

  // Estados para Ações em Massa
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkSearchQuery, setBulkSearchQuery] = useState("");
  const [selectedSkus, setSelectedSkus] = useState(new Set());

  // NOVOS FILTROS DE BUSCA EM MASSA
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const [selectedColorFilter, setSelectedColorFilter] = useState("");

  // NOVOS ESTADOS: Gerenciador de Modelos e Filtros BOM
  const [activeRightTab, setActiveRightTab] = useState("recipe");
  const [editingTemplateName, setEditingTemplateName] = useState("");
  const [editingTemplateLines, setEditingTemplateLines] = useState([]);
  const [renameTemplateInput, setRenameTemplateInput] = useState("");
  const [addSupplyToTemplateId, setAddSupplyToTemplateId] = useState("");
  const [addSupplyToTemplateQty, setAddSupplyToTemplateQty] = useState("1");

  const [bomFilter, setBomFilter] = useState("all"); // "all" | "with_bom" | "no_bom"
  const [skusWithBom, setSkusWithBom] = useState(new Set());
  const [skuBomMap, setSkuBomMap] = useState({}); // sku -> Set of supply_ids
  const [selectedTemplateFilter, setSelectedTemplateFilter] = useState("");
  const [isLoadingBomStatus, setIsLoadingBomStatus] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Estado para feedback de progresso das ações em lote
  const [bulkProgress, setBulkProgress] = useState(null);

  // Limitação de renderização do DOM para performance na busca em massa
  const [renderLimit, setRenderLimit] = useState(100);

  // Extrai categorias e cores de pedra únicas do catálogo (pré-processadas no Contexto)
  const { categories, stoneColors } = useMemo(() => {
    const cats = new Set();
    const colors = new Set();
    
    Object.values(catalog).forEach(item => {
      const cat = item.category;
      if (cat && cat !== "Não Definido" && cat !== "MANUAL") {
        cats.add(cat);
      }
      
      const col = item.stoneColor;
      if (col && col !== "Não Definido" && col !== "MANUAL") {
        colors.add(col);
      }
    });
    
    return {
      categories: Array.from(cats).sort(),
      stoneColors: Array.from(colors).sort()
    };
  }, [catalog]);

  // Carrega modelos salvos no PocketBase (__bom_templates__)
  const loadTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const records = await pb.collection("supplies").getFullList({
        filter: 'name = "__bom_templates__"',
      });

      if (records.length > 0) {
        const record = records[0];
        setTemplatesRecord(record);
        let props = record.stone_properties;
        if (typeof props === "string") {
          try { props = JSON.parse(props); } catch (e) {}
        }
        setTemplates(props?.templates || {});
      } else {
        const record = await pb.collection("supplies").create({
          name: "__bom_templates__",
          sector: "config",
          category: "bom",
          unit: "none",
          min_quantity: 0,
          current_quantity: 0,
          stone_properties: {
            is_config: true,
            templates: {}
          }
        });
        setTemplatesRecord(record);
        setTemplates({});
      }
    } catch (err) {
      console.error("Erro ao carregar modelos BOM:", err);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Carrega SKUs que possuem receitas associadas e mapeia seus insumos
  const loadSkusWithBom = async () => {
    setIsLoadingBomStatus(true);
    try {
      const records = await pb.collection("sku_bom").getFullList({
        fields: "sku,supply_id",
      });
      
      const skus = new Set();
      const tempMap = {};
      
      records.forEach(r => {
        const skuUpper = String(r.sku).trim().toUpperCase();
        skus.add(skuUpper);
        if (!tempMap[skuUpper]) {
          tempMap[skuUpper] = new Set();
        }
        tempMap[skuUpper].add(r.supply_id);
      });
      
      setSkusWithBom(skus);
      setSkuBomMap(tempMap);
    } catch (err) {
      console.error("Erro ao carregar SKUs com receita BOM:", err);
    } finally {
      setIsLoadingBomStatus(false);
    }
  };

  // Helper de fallback hierárquico para recuperar insumos de um SKU
  const getSuppliesForSku = useCallback((sku) => {
    if (!sku) return null;
    const cleanSku = String(sku).trim().toUpperCase();
    
    if (skuBomMap[cleanSku] && skuBomMap[cleanSku].size > 0) {
      return skuBomMap[cleanSku];
    }
    
    const parts = cleanSku.split("-");
    if (parts.length > 1) {
      for (let i = parts.length - 1; i >= 1; i--) {
        const potentialParentSku = parts.slice(0, i).join("-");
        if (skuBomMap[potentialParentSku] && skuBomMap[potentialParentSku].size > 0) {
          return skuBomMap[potentialParentSku];
        }
      }
    }
    return null;
  }, [skuBomMap]);

  const updateLocalSkuBomState = useCallback((sku, newLines) => {
    const skuUpper = String(sku).trim().toUpperCase();
    const newSupplyIds = new Set(newLines.map(l => l.supply_id));
    
    setSkuBomMap(prevMap => ({
      ...prevMap,
      [skuUpper]: newSupplyIds
    }));
    
    setSkusWithBom(prevSkus => {
      const nextSkus = new Set(prevSkus);
      if (newSupplyIds.size > 0) {
        nextSkus.add(skuUpper);
      } else {
        nextSkus.delete(skuUpper);
      }
      return nextSkus;
    });
  }, []);

  useEffect(() => {
    loadTemplates();
    loadSkusWithBom();
  }, []);

  // Salva receita atual como modelo
  const handleSaveTemplate = async (e) => {
    if (e) e.preventDefault();
    const name = newTemplateName.trim();
    if (!name) return;

    if (recipeLines.length === 0) {
      alert("A receita atual está vazia. Não é possível salvar um modelo sem insumos.");
      return;
    }

    const items = recipeLines.map(line => ({
      supply_id: line.supply_id,
      quantity_per_unit: Number(line.quantity_per_unit) || 0
    }));

    const updatedTemplates = {
      ...templates,
      [name]: items
    };

    try {
      if (templatesRecord) {
        await pb.collection("supplies").update(templatesRecord.id, {
          stone_properties: {
            is_config: true,
            templates: updatedTemplates
          }
        });
      }
      setTemplates(updatedTemplates);
      setNewTemplateName("");
      alert(`Modelo "${name}" salvo com sucesso!`);
    } catch (err) {
      alert("Erro ao salvar modelo: " + err.message);
    }
  };

  // Exclui um modelo
  const handleDeleteTemplate = async (templateName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o modelo "${templateName}"?`)) return;

    const updatedTemplates = { ...templates };
    delete updatedTemplates[templateName];

    try {
      if (templatesRecord) {
        await pb.collection("supplies").update(templatesRecord.id, {
          stone_properties: {
            is_config: true,
            templates: updatedTemplates
          }
        });
      }
      setTemplates(updatedTemplates);
      if (selectedTemplateName === templateName) {
        setSelectedTemplateName("");
      }
      alert(`Modelo "${templateName}" excluído.`);
    } catch (err) {
      alert("Erro ao excluir modelo: " + err.message);
    }
  };

  // Aplica um modelo no SKU individual selecionado
  const handleApplyTemplate = async () => {
    if (!selectedSku || !selectedTemplateName) return;
    const templateItems = templates[selectedTemplateName];
    if (!templateItems || templateItems.length === 0) return;

    // Filter out invalid items
    const validTemplateItems = templateItems.filter(item => supplies.some(s => s.id === item.supply_id));
    if (validTemplateItems.length === 0) {
      alert("Todos os insumos deste modelo foram excluídos do sistema. Não há itens válidos para aplicar.");
      return;
    }

    setIsSubmitting(true);
    try {
      let added = 0;
      for (const item of validTemplateItems) {
        const alreadyExists = recipeLines.find(l => l.supply_id === item.supply_id);
        if (!alreadyExists) {
          await bomService.addSupplyToRecipe(selectedSku, item.supply_id, item.quantity_per_unit);
          added++;
        }
      }
      // recarrega receita
      const lines = await bomService.getRecipe(selectedSku);
      setRecipeLines(lines);
      updateLocalSkuBomState(selectedSku, lines);
      alert(`Modelo "${selectedTemplateName}" aplicado! ${added} novos insumos vinculados.`);
    } catch (err) {
      alert("Erro ao aplicar modelo: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Autocomplete do catálogo de produtos (Individual)
  const catalogMatches = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    const term = searchTerm.toLowerCase();
    const hits = [];
    const entries = Object.values(catalog);
    for (const item of entries) {
      if (item.sku?.toLowerCase().includes(term) || item.name?.toLowerCase().includes(term)) {
         hits.push(item);
         if (hits.length >= 8) break;
      }
    }
    return hits;
  }, [searchTerm, catalog]);

  // Busca do catálogo para modo Em Massa (limite estendido para 1000 com filtros de categoria, cor, status BOM e receita aplicada)
  const bulkCatalogMatches = useMemo(() => {
    const hasFilters = 
      bulkSearchQuery.trim().length >= 2 || 
      selectedCategoryFilter !== "" || 
      selectedColorFilter !== "" ||
      selectedTemplateFilter !== "";

    if (!hasFilters) return [];

    const term = bulkSearchQuery.toLowerCase().trim();
    const hits = [];
    const entries = Object.values(catalog);

    for (const item of entries) {
      // 1. Filtro de Texto
      if (term.length >= 2) {
        const matchesText = item.sku?.toLowerCase().includes(term) || item.name?.toLowerCase().includes(term);
        if (!matchesText) continue;
      }

      // 2. Filtro de Categoria
      if (selectedCategoryFilter !== "") {
        if (item.category !== selectedCategoryFilter) continue;
      }

      // 3. Filtro de Cor da Pedra
      if (selectedColorFilter !== "") {
        if (item.stoneColor !== selectedColorFilter) continue;
      }

      // 4. Filtro de Status BOM
      const skuSupplies = getSuppliesForSku(item.sku);
      const hasBom = !!skuSupplies;

      if (bomFilter === "with_bom" && !hasBom) continue;
      if (bomFilter === "no_bom" && hasBom) continue;

      // 5. Filtro de Modelo/Receita Aplicada
      if (selectedTemplateFilter !== "") {
        if (!skuSupplies) continue;
        const templateItems = templates[selectedTemplateFilter];
        if (!templateItems || templateItems.length === 0) continue;
        
        // Apenas insumos válidos (não excluídos)
        const validTemplateItems = templateItems.filter(item => supplies.some(s => s.id === item.supply_id));
        if (validTemplateItems.length === 0) continue;
        
        const matchesTemplate = validTemplateItems.every(tItem => skuSupplies.has(tItem.supply_id));
        if (!matchesTemplate) continue;
      }

      hits.push(item);
      if (hits.length >= 1000) break;
    }
    return hits;
  }, [bulkSearchQuery, catalog, bomFilter, selectedCategoryFilter, selectedColorFilter, selectedTemplateFilter, templates, getSuppliesForSku]);

  // Reseta o limite de renderização sempre que os filtros mudarem para manter digitação fluida
  useEffect(() => {
    setRenderLimit(100);
  }, [bulkSearchQuery, selectedCategoryFilter, selectedColorFilter, bomFilter, selectedTemplateFilter]);

  // Limita a renderização a renderLimit elementos para não sobrecarregar o DOM
  const visibleMatches = useMemo(() => {
    return bulkCatalogMatches.slice(0, renderLimit);
  }, [bulkCatalogMatches, renderLimit]);

  const hasActiveFilters = useMemo(() => {
    return bulkSearchQuery.trim().length >= 2 || 
      selectedCategoryFilter !== "" || 
      selectedColorFilter !== "" ||
      selectedTemplateFilter !== "";
  }, [bulkSearchQuery, selectedCategoryFilter, selectedColorFilter, selectedTemplateFilter]);

  // Carrega receita quando um SKU individual é selecionado
  useEffect(() => {
     if (!selectedSku) {
         setRecipeLines([]);
         return;
      }

      let isMounted = true;
      const load = async () => {
         setIsLoadingRecipe(true);
         try {
            const lines = await bomService.getRecipe(selectedSku);
            if (isMounted) setRecipeLines(lines);
         } catch (err) {
            console.error("Erro ao carregar receita:", err);
         } finally {
            if (isMounted) setIsLoadingRecipe(false);
         }
      };

      load();
      return () => { isMounted = false; };
  }, [selectedSku]);

  const handleSelectProduct = (skuStr) => {
      setSelectedSku(skuStr);
      setSearchTerm("");
  };

  const currentProductData = selectedSku ? findItem(selectedSku) : null;

  // Adiciona insumo manual à receita individual
  const handleAddLine = async (e) => {
    e.preventDefault();
    if (!selectedSku || !selectedSupplyId || !quantity) return;
    
    const alreadyExists = recipeLines.find(l => l.supply_id === selectedSupplyId);
    if (alreadyExists) {
        alert("Este insumo já está na receita. Edite ou exclua o existente.");
        return;
    }

    setIsSubmitting(true);
    try {
       await bomService.addSupplyToRecipe(selectedSku, selectedSupplyId, quantity);
       const lines = await bomService.getRecipe(selectedSku);
       setRecipeLines(lines);
       updateLocalSkuBomState(selectedSku, lines);
       
       setSelectedSupplyId("");
       setQuantity("1");
    } catch (err) {
       alert("Erro ao adicionar na Matriz BOM: " + err.message);
    } finally {
       setIsSubmitting(false);
    }
  };

  // Remove insumo de receita individual
  const handleRemoveLine = async (id, sName) => {
    if (!window.confirm(`Tem certeza que deseja remover ${sName} desta receita?`)) return;
    try {
       await bomService.removeSupplyFromRecipe(id);
       const nextLines = recipeLines.filter(l => l.id !== id);
       setRecipeLines(nextLines);
       updateLocalSkuBomState(selectedSku, nextLines);
    } catch(err) {
       alert("Erro ao remover: " + err.message);
    }
  };

  // Funções de Seleção em Lote
  const handleToggleSelectSku = (sku) => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      if (next.has(sku)) {
        next.delete(sku);
      } else {
        next.add(sku);
      }
      return next;
    });
  };

  const handleSelectAllMatches = () => {
    if (selectedSkus.size === bulkCatalogMatches.length) {
      setSelectedSkus(new Set());
    } else {
      setSelectedSkus(new Set(bulkCatalogMatches.map(m => m.sku)));
    }
  };

  // Aplicação do Modelo em Massa (Loteado em chunks de 15 a 20 itens)
  const handleApplyBulkTemplate = async () => {
    if (selectedSkus.size === 0) {
      alert("Selecione pelo menos um produto final.");
      return;
    }
    if (!selectedTemplateName) {
      alert("Selecione um modelo de receita para aplicar.");
      return;
    }

    const templateItems = templates[selectedTemplateName];
    if (!templateItems || templateItems.length === 0) {
      alert("O modelo selecionado não possui itens.");
      return;
    }

    // Filter out invalid items
    const validTemplateItems = templateItems.filter(item => supplies.some(s => s.id === item.supply_id));
    if (validTemplateItems.length === 0) {
      alert("Todos os insumos deste modelo foram excluídos do sistema. Não há itens válidos para aplicar.");
      return;
    }

    const skusArray = Array.from(selectedSkus);
    const total = skusArray.length;

    setIsSubmitting(true);
    setBulkProgress({ current: 0, total, phase: "Preparando ambiente...", status: "processing" });

    try {
      let totalAdded = 0;
      const chunkSize = 15; // Processa em lotes de 15 itens

      for (let i = 0; i < total; i += chunkSize) {
        const chunk = skusArray.slice(i, i + chunkSize);
        
        setBulkProgress({ 
          current: i, 
          total, 
          phase: `Processando lote ${Math.floor(i / chunkSize) + 1} de ${Math.ceil(total / chunkSize)} (produtos ${i + 1} a ${Math.min(i + chunkSize, total)})...`, 
          status: "processing" 
        });

        await Promise.all(chunk.map(async (sku) => {
          const existingRecipe = await bomService.getRecipe(sku);
          for (const item of validTemplateItems) {
            const exists = existingRecipe.find(r => r.supply_id === item.supply_id);
            if (!exists) {
              await bomService.addSupplyToRecipe(sku, item.supply_id, item.quantity_per_unit);
              totalAdded++;
            }
          }
        }));
      }

      // Atualiza o estado em memória para evitar recarregar tudo do banco!
      setSkuBomMap(prevMap => {
        const nextMap = { ...prevMap };
        skusArray.forEach(sku => {
          const skuUpper = sku.trim().toUpperCase();
          const currentSet = new Set(nextMap[skuUpper] || []);
          validTemplateItems.forEach(item => {
            currentSet.add(item.supply_id);
          });
          nextMap[skuUpper] = currentSet;
        });
        return nextMap;
      });

      setSkusWithBom(prevSkus => {
        const nextSkus = new Set(prevSkus);
        skusArray.forEach(sku => {
          nextSkus.add(sku.trim().toUpperCase());
        });
        return nextSkus;
      });

      setBulkProgress({ current: total, total, phase: "Aplicação concluída com sucesso!", status: "done" });
      alert(`Modelo "${selectedTemplateName}" aplicado com sucesso! ${totalAdded} novos vínculos criados em ${total} produtos.`);
      setSelectedSkus(new Set());
    } catch (err) {
      setBulkProgress(prev => ({ ...prev, phase: "Erro: " + err.message, status: "error" }));
      alert("Erro ao aplicar em massa: " + err.message);
    } finally {
      setIsSubmitting(false);
      // Remove o status de progresso depois de 3 segundos
      setTimeout(() => setBulkProgress(null), 3000);
    }
  };

  // Limpeza de Receita (Zerar) em Massa (Loteado em chunks de 15 a 20 itens)
  const handleClearBulkRecipe = async () => {
    if (selectedSkus.size === 0) {
      alert("Selecione pelo menos um produto final.");
      return;
    }

    if (!window.confirm(`ATENÇÃO: Tem certeza que deseja apagar COMPLETAMENTE a receita BOM de todos os ${selectedSkus.size} produtos selecionados? Esta ação não pode ser desfeita.`)) {
      return;
    }

    const skusArray = Array.from(selectedSkus);
    const total = skusArray.length;

    setIsSubmitting(true);
    setBulkProgress({ current: 0, total, phase: "Preparando ambiente...", status: "processing" });

    try {
      let totalRemoved = 0;
      const chunkSize = 15; // Processa em lotes de 15 itens

      for (let i = 0; i < total; i += chunkSize) {
        const chunk = skusArray.slice(i, i + chunkSize);

        setBulkProgress({ 
          current: i, 
          total, 
          phase: `Limpando lote ${Math.floor(i / chunkSize) + 1} de ${Math.ceil(total / chunkSize)} (produtos ${i + 1} a ${Math.min(i + chunkSize, total)})...`, 
          status: "processing" 
        });

        await Promise.all(chunk.map(async (sku) => {
          const cleanSku = String(sku).trim().toUpperCase();
          const existingRecipe = await pb.collection("sku_bom").getFullList({
            filter: `sku="${cleanSku}"`
          });
          for (const line of existingRecipe) {
            await bomService.removeSupplyFromRecipe(line.id);
            totalRemoved++;
          }
        }));
      }

      // Atualiza o estado em memória para evitar recarregar tudo do banco!
      setSkuBomMap(prevMap => {
        const nextMap = { ...prevMap };
        skusArray.forEach(sku => {
          const skuUpper = sku.trim().toUpperCase();
          delete nextMap[skuUpper];
        });
        return nextMap;
      });

      setSkusWithBom(prevSkus => {
        const nextSkus = new Set(prevSkus);
        skusArray.forEach(sku => {
          nextSkus.delete(sku.trim().toUpperCase());
        });
        return nextSkus;
      });

      setBulkProgress({ current: total, total, phase: "Limpeza de receitas concluída!", status: "done" });
      alert(`Matriz BOM limpa com sucesso! ${totalRemoved} vínculos removidos de ${total} produtos.`);
      setSelectedSkus(new Set());
    } catch (err) {
      setBulkProgress(prev => ({ ...prev, phase: "Erro: " + err.message, status: "error" }));
      alert("Erro ao limpar em massa: " + err.message);
    } finally {
      setIsSubmitting(false);
      // Remove o status de progresso depois de 3 segundos
      setTimeout(() => setBulkProgress(null), 3000);
    }
  };

  // ================= FUNÇÕES DO GERENCIADOR DE MODELOS (EDITOR) =================

  const handleSaveEditedTemplate = async () => {
    const name = renameTemplateInput.trim();
    const oldName = editingTemplateName;
    if (!name) {
      alert("O nome do modelo não pode ser vazio.");
      return;
    }

    if (editingTemplateLines.length === 0) {
      alert("O modelo deve conter pelo menos um insumo.");
      return;
    }

    const updatedTemplates = { ...templates };

    if (oldName && oldName !== name) {
      delete updatedTemplates[oldName];
    }

    updatedTemplates[name] = editingTemplateLines.map(line => ({
      supply_id: line.supply_id,
      quantity_per_unit: Number(line.quantity_per_unit) || 0
    }));

    setIsSubmitting(true);
    try {
      if (templatesRecord) {
        await pb.collection("supplies").update(templatesRecord.id, {
          stone_properties: {
            is_config: true,
            templates: updatedTemplates
          }
        });
      }
      setTemplates(updatedTemplates);
      setEditingTemplateName(name);
      alert(`Modelo "${name}" atualizado com sucesso!`);
    } catch (err) {
      alert("Erro ao salvar alterações no modelo: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApplyTemplateFromEditor = async () => {
    if (!selectedSku) {
      alert("Selecione um produto final na barra da esquerda primeiro.");
      return;
    }
    if (editingTemplateLines.length === 0) {
      alert("O modelo selecionado não possui itens.");
      return;
    }

    setIsSubmitting(true);
    try {
      let added = 0;
      for (const item of editingTemplateLines) {
        const alreadyExists = recipeLines.find(l => l.supply_id === item.supply_id);
        if (!alreadyExists) {
          await bomService.addSupplyToRecipe(selectedSku, item.supply_id, item.quantity_per_unit);
          added++;
        }
      }
      const lines = await bomService.getRecipe(selectedSku);
      setRecipeLines(lines);
      updateLocalSkuBomState(selectedSku, lines);
      alert(`Modelo "${editingTemplateName}" aplicado ao SKU ${selectedSku}! ${added} novos insumos vinculados.`);
    } catch (err) {
      alert("Erro ao aplicar modelo: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddSupplyToTemplate = () => {
    if (!addSupplyToTemplateId) return;
    const exists = editingTemplateLines.find(l => l.supply_id === addSupplyToTemplateId);
    if (exists) {
      alert("Este insumo já está no modelo.");
      return;
    }

    const supplyInfo = supplies.find(s => s.id === addSupplyToTemplateId);
    if (!supplyInfo) return;

    setEditingTemplateLines(prev => [
      ...prev,
      {
        supply_id: addSupplyToTemplateId,
        quantity_per_unit: Number(addSupplyToTemplateQty) || 1,
        expand: { supply_id: supplyInfo }
      }
    ]);
    setAddSupplyToTemplateId("");
    setAddSupplyToTemplateQty("1");
  };

  const handleRemoveSupplyFromTemplate = (supplyId) => {
    setEditingTemplateLines(prev => prev.filter(l => l.supply_id !== supplyId));
  };

  const handleUpdateQtyInTemplate = (supplyId, qty) => {
    setEditingTemplateLines(prev => prev.map(l => {
      if (l.supply_id === supplyId) {
        return { ...l, quantity_per_unit: Number(qty) || 0 };
      }
      return l;
    }));
  };

  const handleDeleteTemplateFromEditor = async () => {
    if (!editingTemplateName) return;
    if (!window.confirm(`Tem certeza que deseja excluir permanentemente o modelo "${editingTemplateName}"?`)) return;

    const updatedTemplates = { ...templates };
    delete updatedTemplates[editingTemplateName];

    setIsSubmitting(true);
    try {
      if (templatesRecord) {
        await pb.collection("supplies").update(templatesRecord.id, {
          stone_properties: {
            is_config: true,
            templates: updatedTemplates
          }
        });
      }
      setTemplates(updatedTemplates);
      setEditingTemplateName("");
      setEditingTemplateLines([]);
      setRenameTemplateInput("");
      alert(`Modelo excluído com sucesso.`);
    } catch (err) {
      alert("Erro ao excluir modelo: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectTemplateForEditing = (name) => {
    setEditingTemplateName(name);
    setRenameTemplateInput(name);
    const items = templates[name] || [];
    // Only keep items whose supply exists in the supplies list!
    const validItems = items.filter(item => supplies.some(s => s.id === item.supply_id));
    const expandedItems = validItems.map(item => {
      const supplyInfo = supplies.find(s => s.id === item.supply_id);
      return {
        ...item,
        expand: { supply_id: supplyInfo }
      };
    });
    setEditingTemplateLines(expandedItems);
  };

  const handleCreateEmptyTemplate = () => {
    setEditingTemplateName("Nova Receita");
    setRenameTemplateInput("");
    setEditingTemplateLines([]);
  };

  const isInitialLoading = isLoadingBomStatus || isLoadingTemplates || loadingCatalog || isLoadingSupplies;

  if (isInitialLoading) {
    return (
      <div className="flex-1 flex flex-col justify-center items-center h-full min-h-[400px] bg-slate-50 gap-4 p-8">
        <div className="relative flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          <Database size={24} className="text-blue-500 absolute" />
        </div>
        <div className="text-center">
          <h4 className="font-bold text-slate-800 text-base">Carregando Matriz BOM</h4>
          <p className="text-xs text-slate-500 mt-1">Obtendo receitas, insumos e catálogo de produtos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-slate-50 gap-4 overflow-hidden p-4 lg:p-6">
       
       {/* Esquerda: Buscador de Produtos */}
       <div className="w-full lg:w-1/3 flex flex-col gap-4 min-h-0 relative">
          {isSubmitting && (
            <div className="absolute inset-0 bg-slate-50/50 backdrop-blur-[0.5px] z-50 rounded-xl cursor-not-allowed"></div>
          )}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-full overflow-hidden">
             
             {/* Sub-abas de Busca: Individual vs Em Massa */}
             <div className="p-4 border-b border-slate-100 shrink-0 bg-slate-50 flex flex-col gap-3">
               <h3 className="font-bold text-slate-800 flex items-center gap-2">
                 <Database size={18} className="text-blue-600"/> Localizar Final
               </h3>
               <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-bold">
                 <button
                   type="button"
                   onClick={() => { setIsBulkMode(false); setSelectedSkus(new Set()); }}
                   className={`flex-1 py-1.5 rounded-md transition-colors ${!isBulkMode ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                 >
                   Busca Individual
                 </button>
                 <button
                   type="button"
                   onClick={() => { setIsBulkMode(true); setSelectedSku(null); }}
                   className={`flex-1 py-1.5 rounded-md transition-colors ${isBulkMode ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                 >
                   Ações em Massa
                 </button>
               </div>
             </div>

             {/* MODO BUSCA INDIVIDUAL */}
             {!isBulkMode && (
               <div className="p-4 shrink-0 relative flex flex-col gap-2">
                   {loadingCatalog && <span className="text-xs text-blue-500 absolute right-6 top-10 animate-pulse">Carregando catálogo...</span>}
                   
                   <div className="relative">
                     <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                     <input 
                       type="text" 
                       placeholder="Digite o SKU ou Nome do colar..."
                       value={searchTerm}
                       onChange={e => { setSearchTerm(e.target.value); setSelectedSku(null); }}
                       autoComplete="off"
                       className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                     />
                   </div>
                   
                   {/* Resultados da busca individual */}
                   {searchTerm.length >= 2 && !selectedSku && (
                      <div className="absolute top-full left-4 right-4 mt-1 bg-white shadow-xl border border-slate-100 rounded-lg z-50 max-h-64 overflow-y-auto">
                         {catalogMatches.length === 0 ? (
                             <div className="p-4 text-sm text-slate-500 text-center">Nenhum produto achado :(</div>
                         ) : (
                             catalogMatches.map(item => (
                               <button
                                 key={item.sku}
                                 onClick={() => handleSelectProduct(item.sku)}
                                 className="w-full text-left p-3 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-55 last:border-0 transition-colors"
                               >
                                  <div className="h-10 w-10 shrink-0 bg-slate-100 rounded flex items-center justify-center overflow-hidden">
                                     {item.image ? <img src={item.image} alt="Produto" className="object-cover w-full h-full" /> : <Package size={16} />}
                                  </div>
                                  <div className="overflow-hidden flex-1">
                                     <div className="font-bold text-sm text-slate-800 truncate">{item.sku}</div>
                                     <div className="text-xs text-slate-500 truncate">{item.name}</div>
                                  </div>
                               </button>
                             ))
                         )}
                      </div>
                   )}
               </div>
             )}

             {/* MODO BUSCA EM MASSA */}
             {isBulkMode && (
               <div className="p-4 flex-1 flex flex-col overflow-hidden gap-3">
                 <div className="relative shrink-0">
                   <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                   <input 
                     type="text" 
                     placeholder="Digite sigla (Ex: ATL, ENG, COL)..."
                     value={bulkSearchQuery}
                     onChange={e => { setBulkSearchQuery(e.target.value); setSelectedSkus(new Set()); }}
                     autoComplete="off"
                     className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                   />
                 </div>

                 {/* NOVOS FILTROS: Categoria, Cor da Pedra e Modelo/Receita */}
                 <div className="grid grid-cols-3 gap-2 shrink-0">
                   <div className="flex flex-col gap-1">
                     <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Categoria</label>
                     <select
                       value={selectedCategoryFilter}
                       onChange={e => { setSelectedCategoryFilter(e.target.value); setSelectedSkus(new Set()); }}
                       className="w-full bg-white border border-slate-300 text-slate-700 text-[11px] font-semibold rounded-lg p-1.5 outline-none focus:ring-2 focus:ring-blue-500 shadow-sm cursor-pointer"
                     >
                       <option value="">Todas</option>
                       {categories.map(c => (
                         <option key={c} value={c}>{c}</option>
                       ))}
                     </select>
                   </div>
                   <div className="flex flex-col gap-1">
                     <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Cor da Pedra</label>
                     <select
                       value={selectedColorFilter}
                       onChange={e => { setSelectedColorFilter(e.target.value); setSelectedSkus(new Set()); }}
                       className="w-full bg-white border border-slate-300 text-slate-700 text-[11px] font-semibold rounded-lg p-1.5 outline-none focus:ring-2 focus:ring-blue-500 shadow-sm cursor-pointer"
                     >
                       <option value="">Todas</option>
                       {stoneColors.map(col => (
                         <option key={col} value={col}>{col}</option>
                       ))}
                     </select>
                   </div>
                   <div className="flex flex-col gap-1">
                     <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Modelo/Receita</label>
                     <select
                       value={selectedTemplateFilter}
                       onChange={e => { setSelectedTemplateFilter(e.target.value); setSelectedSkus(new Set()); }}
                       className="w-full bg-white border border-slate-300 text-slate-700 text-[11px] font-semibold rounded-lg p-1.5 outline-none focus:ring-2 focus:ring-blue-500 shadow-sm cursor-pointer"
                     >
                       <option value="">Todos</option>
                       {Object.keys(templates).sort().map(name => (
                         <option key={name} value={name}>{name}</option>
                       ))}
                     </select>
                   </div>
                 </div>

                 {/* Filtro de Status do BOM */}
                 {hasActiveFilters && (
                   <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10px] font-bold shrink-0">
                     <button
                       type="button"
                       onClick={() => { setBomFilter("all"); setSelectedSkus(new Set()); }}
                       className={`flex-1 py-1 rounded-md transition-colors ${bomFilter === "all" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                     >
                       Todos ({bulkCatalogMatches.length})
                     </button>
                     <button
                       type="button"
                       onClick={() => { setBomFilter("no_bom"); setSelectedSkus(new Set()); }}
                       className={`flex-1 py-1 rounded-md transition-colors ${bomFilter === "no_bom" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                     >
                       Sem BOM
                     </button>
                     <button
                       type="button"
                       onClick={() => { setBomFilter("with_bom"); setSelectedSkus(new Set()); }}
                       className={`flex-1 py-1 rounded-md transition-colors ${bomFilter === "with_bom" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
                     >
                       Com BOM
                     </button>
                   </div>
                 )}

                 {hasActiveFilters && (
                   <div className="flex-1 flex flex-col overflow-hidden border border-slate-200 rounded-lg min-h-0 bg-white">
                     {/* Header Selecionar Todos */}
                     <div className="bg-slate-50 border-b border-slate-200 p-2.5 flex items-center justify-between shrink-0 text-xs">
                       <label className="flex items-center gap-2 font-bold text-slate-700 cursor-pointer">
                         <input
                           type="checkbox"
                           checked={bulkCatalogMatches.length > 0 && selectedSkus.size === bulkCatalogMatches.length}
                           onChange={handleSelectAllMatches}
                           className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                         />
                         Selecionar Todos
                       </label>
                       <span className="text-[10px] text-slate-400 font-bold uppercase">
                         {selectedSkus.size} de {bulkCatalogMatches.length} marcados
                       </span>
                     </div>

                      {/* Lista com Scroll */}
                      <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                        {bulkCatalogMatches.length === 0 ? (
                          <div className="p-8 text-center text-xs text-slate-400">
                            Nenhum produto correspondente encontrado.
                          </div>
                        ) : (
                          <>
                            {visibleMatches.map(item => {
                              const isSelected = selectedSkus.has(item.sku);
                              const skuSupplies = getSuppliesForSku(item.sku);
                              const hasBom = !!skuSupplies;
                              const appliedTemplates = [];
                              if (skuSupplies && skuSupplies.size > 0) {
                                Object.entries(templates).forEach(([name, templateItems]) => {
                                  if (templateItems && templateItems.length > 0) {
                                    // Filter out invalid items
                                    const validTemplateItems = templateItems.filter(item => supplies.some(s => s.id === item.supply_id));
                                    if (validTemplateItems.length > 0) {
                                      const matchesAll = validTemplateItems.every(tItem => skuSupplies.has(tItem.supply_id));
                                      if (matchesAll) {
                                        appliedTemplates.push(name);
                                      }
                                    }
                                  }
                                });
                              }
                              return (
                                <label
                                  key={item.sku}
                                  className={`p-3 flex items-center gap-3 hover:bg-slate-50 cursor-pointer transition-colors ${isSelected ? "bg-blue-50/20" : ""}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggleSelectSku(item.sku)}
                                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                  <div className="h-8 w-8 shrink-0 bg-slate-100 rounded overflow-hidden flex items-center justify-center relative">
                                    {item.image ? <img src={item.image} alt="Produto" className="object-cover w-full h-full" /> : <Package size={12} />}
                                  </div>
                                  <div className="overflow-hidden flex-1">
                                    <div className="font-bold text-xs text-slate-800 flex items-center gap-1.5 truncate">
                                      {item.sku}
                                      {hasBom && (
                                        <span className="text-[8px] bg-emerald-100 text-emerald-800 px-1 py-0.2 rounded font-bold">BOM</span>
                                      )}
                                    </div>
                                    <div className="text-[10px] text-slate-500 truncate">{item.name}</div>
                                    {appliedTemplates.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {appliedTemplates.map(name => (
                                          <span 
                                            key={name}
                                            className="text-[8px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100 font-semibold"
                                          >
                                            {name}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </label>
                              );
                            })}

                            {bulkCatalogMatches.length > renderLimit && (
                              <button
                                type="button"
                                onClick={() => setRenderLimit(prev => prev + 100)}
                                className="w-full py-3 bg-slate-50 hover:bg-slate-100 text-blue-600 font-bold text-[10px] text-center border-t border-slate-100 transition-colors uppercase tracking-wider"
                              >
                                Mostrar mais ({bulkCatalogMatches.length - renderLimit} itens restantes)
                              </button>
                            )}
                          </>
                        )}
                      </div>
                   </div>
                 )}
                 {!hasActiveFilters && (
                   <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center opacity-60">
                     <Database size={40} className="mb-3 stroke-1"/>
                     <p className="text-xs">Digite pelo menos 2 caracteres ou selecione um filtro (Categoria / Cor) para listar os produtos em massa.</p>
                   </div>
                 )}
               </div>
             )}

             {/* Placeholder se individual não selecionado */}
             {!isBulkMode && !selectedSku && (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center opacity-60">
                   <Database size={48} className="mb-4 stroke-1"/>
                   <p>Pesquise e selecione um SKU acima para montar sua receita BOM.</p>
                </div>
             )}

             {/* Card do Produto Selecionado (Individual) */}
             {!isBulkMode && selectedSku && currentProductData && (
                <div className="flex-1 p-4 overflow-y-auto min-h-0 bg-white">
                   <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex flex-col items-center text-center gap-3 relative">
                      <button 
                         onClick={() => setSelectedSku(null)}
                         className="absolute top-2 right-2 text-slate-400 hover:text-slate-700" title="Limpar"
                      >
                         <X size={16} />
                      </button>
                      <div className="h-24 w-24 bg-white rounded-lg shadow flex items-center justify-center overflow-hidden shrink-0">
                         {currentProductData.image ? 
                           <img src={currentProductData.image} alt={currentProductData.sku} className="object-contain h-full w-full" /> 
                         : 
                           <Package size={32} className="text-slate-300"/>
                         }
                      </div>
                      <div>
                         <h4 className="font-black text-xl text-slate-800">{currentProductData.sku}</h4>
                         <p className="text-sm font-medium text-slate-600 mt-1">{currentProductData.name}</p>
                         <div className="mt-3 flex gap-2 justify-center flex-wrap">
                            {(() => {
                               const tagsSource = currentProductData.tags;
                               if (!tagsSource) return null;
                               try {
                                 const parsed = typeof tagsSource === "string" && tagsSource.trim().startsWith("[")
                                   ? JSON.parse(tagsSource.replace(/\\"/g, '"'))
                                   : tagsSource;
                                 if (Array.isArray(parsed)) {
                                   return parsed.map((t, i) => (
                                     <span key={i} className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded text-slate-500 font-semibold shadow-sm">
                                       {t.group ? `${t.group}: ${t.value}` : (t.value || t)}
                                     </span>
                                   ));
                                 }
                               } catch (e) {}
                               // Fallback para string simples separada por vírgulas
                               return String(tagsSource).split(',').map((t, i) => (
                                 <span key={i} className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded text-slate-500 font-semibold shadow-sm">
                                    {t.trim()}
                                 </span>
                               ));
                             })()}
                         </div>
                      </div>
                   </div>
                </div>
             )}
          </div>
       </div>

       {/* Direita: Receita Line Items */}
       <div className="w-full lg:w-2/3 flex flex-col gap-4 min-h-0">
          {!isBulkMode ? (
             // ================= MODO INDIVIDUAL =================
             <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-full overflow-hidden bg-white relative">
                {isSubmitting && (
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex flex-col items-center justify-center z-50 gap-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                    <p className="text-xs font-bold text-slate-700">Salvando alterações no BOM...</p>
                  </div>
                )}
                
                {/* Abas no topo do painel direito */}
                <div className="border-b border-slate-200 bg-slate-50 flex items-center justify-between shrink-0 p-1">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setActiveRightTab("recipe")}
                      className={`px-4 py-2 text-xs font-bold transition-all border-b-2 ${activeRightTab === "recipe" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                    >
                      Receita do Item {selectedSku ? `(${selectedSku})` : ""}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveRightTab("templates")}
                      className={`px-4 py-2 text-xs font-bold transition-all border-b-2 ${activeRightTab === "templates" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                    >
                      Gerenciar Modelos (Templates)
                    </button>
                  </div>
                  {activeRightTab === "recipe" && selectedSku && (
                     <span className="text-xs bg-emerald-100 text-emerald-800 font-bold px-2 py-1 rounded-full mr-2">
                        {recipeLines.length} insumos mapeados
                     </span>
                  )}
                </div>

                {activeRightTab === "recipe" ? (
                  !selectedSku ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center opacity-60">
                       <Database size={48} className="mb-4 stroke-1"/>
                       <p className="text-sm font-semibold">Selecione um SKU na busca à esquerda para gerenciar sua receita.</p>
                    </div>
                  ) : (
                    <>
                      {/* Barra de Modelos de Receita (Templates) Rápida */}
                      <div className="bg-slate-50/50 border-b border-slate-100 p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shrink-0">
                        {/* Carregar Modelo */}
                        <div className="flex items-center gap-2 w-full md:max-w-sm">
                          <select
                            value={selectedTemplateName}
                            onChange={e => setSelectedTemplateName(e.target.value)}
                            className="flex-1 p-2 text-xs border border-slate-300 rounded focus:border-blue-500 bg-white outline-none"
                          >
                            <option value="">-- Carregar Modelo Salvo --</option>
                            {Object.keys(templates).sort().map(name => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={handleApplyTemplate}
                            disabled={!selectedTemplateName || isSubmitting}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-2 rounded shadow transition-colors disabled:opacity-50"
                          >
                            Aplicar
                          </button>
                          {selectedTemplateName && (
                            <button
                              type="button"
                              onClick={() => handleDeleteTemplate(selectedTemplateName)}
                              className="text-slate-400 hover:text-red-500 p-1"
                              title="Excluir este modelo"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>

                        {/* Criar Novo Modelo a partir da receita atual */}
                        <form onSubmit={handleSaveTemplate} className="flex items-center gap-2 w-full md:max-w-xs">
                          <input
                            type="text"
                            placeholder="Salvar receita como modelo..."
                            required
                            value={newTemplateName}
                            onChange={e => setNewTemplateName(e.target.value)}
                            className="flex-1 p-2 text-xs border border-slate-300 rounded focus:border-blue-500 outline-none"
                          />
                          <button
                            type="submit"
                            disabled={recipeLines.length === 0}
                            className="bg-slate-800 hover:bg-black text-white text-xs font-bold px-3 py-2 rounded shadow transition-colors disabled:opacity-50 whitespace-nowrap"
                          >
                            Salvar
                          </button>
                        </form>
                      </div>

                      {/* Formulário de ADICIONAR */}
                      <div className="p-4 border-b border-slate-100 shrink-0 bg-white">
                         <form onSubmit={handleAddLine} className="flex flex-col sm:flex-row gap-3 items-end">
                            <div className="flex-1 w-full relative">
                               <label className="block text-[11px] uppercase font-bold text-slate-500 mb-1">
                                 Buscar Insumo para Vínculo
                               </label>
                               <select
                                  required
                                  value={selectedSupplyId}
                                  onChange={e => setSelectedSupplyId(e.target.value)}
                                  className="w-full p-2 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:bg-slate-50 disabled:text-slate-400"
                                  disabled={!selectedSku || isLoadingSupplies}
                               >
                                  <option value="" disabled>-- Selecione o insumo --</option>
                                  {supplies && Array.from(new Set(supplies.map(s => s.sector))).sort().map(sector => (
                                     <optgroup key={sector} label={sector.toUpperCase() || 'GERAL'}>
                                        {supplies.filter(s => s.sector === sector).sort((a,b) => a.name.localeCompare(b.name)).map(s => (
                                           <option key={s.id} value={s.id}>
                                              {s.name} ({s.category})
                                           </option>
                                        ))}
                                     </optgroup>
                                  ))}
                               </select>
                            </div>
                            <div className="w-full sm:w-28 relative">
                               <label className="block text-[11px] uppercase font-bold text-slate-500 mb-1">
                                 Qtd. por Produto
                               </label>
                               <input 
                                  type="number" 
                                  required 
                                  min="0"
                                  step="any"
                                  value={quantity}
                                  onChange={e => setQuantity(e.target.value)}
                                  className="w-full p-2 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                               />
                            </div>
                            <button
                               type="submit"
                               disabled={isSubmitting || !selectedSku}
                               className="w-full sm:w-auto px-4 py-2 bg-slate-800 hover:bg-black text-white font-medium rounded flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                            >
                               {isSubmitting ? <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></span> : <Plus size={18}/>}
                               Vincular
                            </button>
                         </form>
                      </div>

                      {/* Tabela de Receita */}
                      <div className="flex-1 overflow-x-auto bg-white p-4">
                         {isLoadingRecipe ? (
                             <div className="h-full flex items-center justify-center text-slate-500 animate-pulse">Carregando Matriz BOM...</div>
                         ) : recipeLines.length === 0 ? (
                             <div className="h-full flex flex-col items-center justify-center text-slate-400 opacity-70">
                                <Database size={32} className="mb-2"/>
                                <p>Este produto não consome nenhum insumo listado ainda.</p>
                             </div>
                         ) : (
                             <table className="w-full text-left border-collapse min-w-[500px]">
                                <thead className="bg-slate-50">
                                   <tr>
                                      <th className="p-3 text-xs font-semibold text-slate-500 uppercase">Insumo</th>
                                      <th className="p-3 text-xs font-semibold text-slate-500 uppercase">Setor</th>
                                      <th className="p-3 text-xs font-semibold text-slate-500 uppercase text-right">Qtd Consumida</th>
                                      <th className="p-3 text-xs text-center w-16">❌</th>
                                   </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                   {recipeLines.map(line => {
                                      const supply = line.expand?.supply_id;
                                      if (!supply) return null;
                                      return (
                                         <tr key={line.id} className="hover:bg-slate-50">
                                            <td className="p-3">
                                               <div className="font-bold text-slate-800">{supply.name}</div>
                                               <div className="text-[10px] text-slate-500">{supply.category}</div>
                                            </td>
                                            <td className="p-3">
                                               <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-bold uppercase">{supply.sector}</span>
                                            </td>
                                            <td className="p-3 text-right">
                                               <span className="font-black text-blue-600 text-base">{line.quantity_per_unit}</span>
                                               <span className="text-xs text-slate-500 ml-1">{supply.unit}</span>
                                            </td>
                                            <td className="p-3 text-center">
                                               <button 
                                                  onClick={() => handleRemoveLine(line.id, supply.name)}
                                                  className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-slate-100 rounded transition" title="Remover Vínculo"
                                               >
                                                  <Trash2 size={16}/>
                                               </button>
                                            </td>
                                         </tr>
                                      )
                                   })}
                                </tbody>
                             </table>
                         )}
                      </div>
                    </>
                  )
                ) : (
                  // ================= ABA DE GERENCIAR MODELOS (EDITOR) =================
                  <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0 bg-white">
                    {/* Lista de Modelos (Esquerda do painel) */}
                    <div className="w-full md:w-1/3 border-r border-slate-100 flex flex-col overflow-y-auto p-4 gap-2 bg-slate-50/50">
                      <h4 className="text-[10px] font-extrabold uppercase text-slate-400 mb-2 tracking-wider">Modelos Disponíveis</h4>
                      
                      <button
                        type="button"
                        onClick={handleCreateEmptyTemplate}
                        className="w-full flex items-center justify-center gap-1.5 py-2 px-3 mb-2 border border-dashed border-blue-400 hover:border-blue-600 text-blue-600 hover:bg-blue-50/50 rounded-lg text-xs font-bold transition-all shadow-sm shrink-0"
                      >
                        <Plus size={14} /> Nova Receita (Modelo)
                      </button>

                      {Object.keys(templates).length === 0 ? (
                        <div className="text-xs text-slate-400 text-center py-6">Nenhum modelo cadastrado.</div>
                      ) : (
                        Object.keys(templates).sort().map(name => (
                          <button
                            key={name}
                            type="button"
                            onClick={() => handleSelectTemplateForEditing(name)}
                            className={`w-full text-left p-3 rounded-lg text-xs font-bold transition-all border ${editingTemplateName === name ? 'bg-blue-600 text-white border-blue-600 shadow' : 'bg-white hover:bg-slate-100 text-slate-700 border-slate-200'}`}
                          >
                            <div className="truncate">{name}</div>
                            <div className={`text-[10px] font-normal mt-1 ${editingTemplateName === name ? 'text-blue-100' : 'text-slate-400'}`}>
                              {templates[name]?.length || 0} insumos salvos
                            </div>
                          </button>
                        ))
                      )}
                    </div>

                    {/* Detalhes / Edição de Modelo (Direita do painel) */}
                    <div className="flex-1 flex flex-col overflow-hidden p-4 min-h-0">
                      {editingTemplateName ? (
                        <div className="flex-1 flex flex-col min-h-0 gap-4">
                          {/* Cabeçalho do Editor de Modelo */}
                          <div className="flex flex-col gap-2 border-b border-slate-100 pb-3 shrink-0">
                            <label className="text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">Nome do Modelo</label>
                            <div className="flex gap-2 items-center">
                              <input
                                type="text"
                                placeholder="Digite o nome do modelo/receita..."
                                value={renameTemplateInput}
                                onChange={e => setRenameTemplateInput(e.target.value)}
                                className="flex-1 p-2 text-sm font-bold border border-slate-300 rounded focus:border-blue-500 outline-none"
                              />
                              <button
                                type="button"
                                onClick={handleSaveEditedTemplate}
                                disabled={isSubmitting}
                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-2 rounded shadow transition-colors whitespace-nowrap"
                              >
                                Salvar Alterações
                              </button>
                              <button
                                type="button"
                                onClick={handleDeleteTemplateFromEditor}
                                disabled={isSubmitting}
                                className="bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold p-2 rounded border border-red-200 transition-colors"
                                title="Excluir Modelo"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          {/* Botão de Aplicar Rápido ao SKU Ativo */}
                          {selectedSku && (
                            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 flex justify-between items-center shrink-0">
                              <div className="text-xs text-emerald-800">
                                Aba ativa: <strong className="font-black">{selectedSku}</strong>. Quer aplicar este modelo a ele?
                              </div>
                              <button
                                type="button"
                                onClick={handleApplyTemplateFromEditor}
                                disabled={isSubmitting}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded shadow transition-colors whitespace-nowrap"
                              >
                                Carregar no SKU Atual
                              </button>
                            </div>
                          )}

                          {/* Adicionar Insumo ao Modelo */}
                          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 shrink-0 flex flex-col sm:flex-row gap-2 items-end">
                            <div className="flex-1 w-full">
                              <label className="block text-[10px] font-extrabold uppercase text-slate-500 mb-1">Adicionar Insumo</label>
                              <select
                                value={addSupplyToTemplateId}
                                onChange={e => setAddSupplyToTemplateId(e.target.value)}
                                className="w-full p-2 text-xs border border-slate-300 rounded focus:border-blue-500 bg-white outline-none"
                              >
                                <option value="">-- Selecione o Insumo --</option>
                                {supplies && Array.from(new Set(supplies.map(s => s.sector))).sort().map(sector => (
                                  <optgroup key={sector} label={sector.toUpperCase() || 'GERAL'}>
                                    {supplies.filter(s => s.sector === sector).sort((a,b) => a.name.localeCompare(b.name)).map(s => (
                                      <option key={s.id} value={s.id}>
                                        {s.name} ({s.category})
                                      </option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                            </div>
                            <div className="w-full sm:w-24">
                              <label className="block text-[10px] font-extrabold uppercase text-slate-500 mb-1">Qtd</label>
                              <input
                                type="number"
                                min="0"
                                step="any"
                                value={addSupplyToTemplateQty}
                                onChange={e => setAddSupplyToTemplateQty(e.target.value)}
                                className="w-full p-2 text-xs border border-slate-300 rounded focus:border-blue-500 outline-none"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleAddSupplyToTemplate}
                              className="bg-slate-800 hover:bg-black text-white text-xs font-bold px-3 py-2 rounded shadow transition-colors whitespace-nowrap"
                            >
                              Incluir
                            </button>
                          </div>

                          {/* Lista de Insumos do Modelo */}
                          <div className="flex-1 overflow-y-auto border border-slate-100 rounded-lg">
                            {editingTemplateLines.length === 0 ? (
                              <div className="p-8 text-center text-xs text-slate-400">Nenhum insumo incluído neste modelo.</div>
                            ) : (
                              <table className="w-full text-left border-collapse text-xs">
                                <thead className="bg-slate-50 sticky top-0">
                                  <tr>
                                    <th className="p-2.5 font-bold text-slate-500 uppercase">Insumo</th>
                                    <th className="p-2.5 font-bold text-slate-500 uppercase text-right w-24">Qtd</th>
                                    <th className="p-2.5 text-center w-12">❌</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                  {editingTemplateLines.map(line => {
                                    const supply = line.expand?.supply_id;
                                    if (!supply) return null;
                                    return (
                                      <tr key={line.supply_id} className="hover:bg-slate-50">
                                        <td className="p-2.5">
                                          <div className="font-bold text-slate-800">{supply.name}</div>
                                          <div className="text-[10px] text-slate-400">{supply.category} | {supply.sector}</div>
                                        </td>
                                        <td className="p-2.5 text-right">
                                          <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            value={line.quantity_per_unit}
                                            onChange={e => handleUpdateQtyInTemplate(line.supply_id, e.target.value)}
                                            className="w-16 p-1 border border-slate-300 rounded text-right font-bold text-blue-600"
                                          />
                                        </td>
                                        <td className="p-2.5 text-center">
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveSupplyFromTemplate(line.supply_id)}
                                            className="text-slate-300 hover:text-red-500 p-1"
                                          >
                                            <Trash2 size={14} />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center opacity-60">
                          <Database size={40} className="mb-3 stroke-1" />
                          <p className="text-xs">Selecione um modelo à esquerda para editar seus ingredientes ou criar novas receitas padronizadas.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
             </div>
          ) : (
             // ================= MODO EM MASSA =================
             <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-full overflow-hidden bg-white">
                
                <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
                  <h3 className="font-bold text-slate-800">
                    Ações em Massa ({selectedSkus.size} produtos selecionados)
                  </h3>
                  <span className="text-xs bg-blue-100 text-blue-800 font-bold px-2 py-1 rounded-full">
                    Modo Lote Ativo
                  </span>
                </div>

                {bulkProgress ? (
                  /* Painel de Progresso das Operações em Lote */
                  <div className="p-8 flex-1 flex flex-col justify-center items-center max-w-md mx-auto text-center gap-6">
                    <div className="relative flex items-center justify-center">
                      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
                      <Database size={24} className="text-blue-500 absolute" />
                    </div>
                    <div className="w-full flex flex-col gap-3">
                      <div>
                        <h4 className="font-bold text-slate-800 text-base">Executando Operação em Massa</h4>
                        <p className="text-xs text-slate-500 mt-1">{bulkProgress.phase}</p>
                      </div>
                      <div className="w-full bg-slate-200 h-3 rounded-full overflow-hidden shadow-inner">
                        <div 
                          className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between items-center text-xs font-bold text-slate-600 px-1">
                        <span>{bulkProgress.current} de {bulkProgress.total} processados</span>
                        <span>{Math.round((bulkProgress.current / bulkProgress.total) * 100)}%</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 flex-1 flex flex-col justify-center items-center max-w-md mx-auto text-center gap-6 overflow-y-auto">
                    <Database size={48} className="text-blue-500 stroke-1 animate-bounce" style={{ animationDuration: '3s' }} />
                    <div>
                      <h4 className="font-bold text-slate-800 text-base">O que deseja fazer com as peças selecionadas?</h4>
                      <p className="text-xs text-slate-400 mt-1">
                        Selecione um dos modelos pré-salvos abaixo para aplicar a receita em lote ou limpe a receita de todos de uma vez.
                      </p>
                    </div>

                    {/* Ação 1: Aplicar Modelo */}
                    <div className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col gap-3">
                      <label className="block text-[10px] font-extrabold uppercase text-slate-500 text-left">
                        Escolha o Modelo de Receita
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={selectedTemplateName}
                          onChange={e => setSelectedTemplateName(e.target.value)}
                          className="flex-1 p-2 text-xs border border-slate-300 rounded focus:border-blue-500 bg-white outline-none"
                          disabled={selectedSkus.size === 0}
                        >
                          <option value="">-- Selecione o Modelo --</option>
                          {Object.keys(templates).sort().map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleApplyBulkTemplate}
                          disabled={!selectedTemplateName || selectedSkus.size === 0 || isSubmitting}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded-lg shadow transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                          Aplicar Modelo
                        </button>
                      </div>
                    </div>

                    {/* Ação 2: Limpar Receitas */}
                    <div className="w-full bg-red-50/50 border border-red-200 rounded-xl p-4 flex flex-col gap-3">
                      <div className="text-left">
                        <h5 className="font-bold text-red-800 text-xs">Limpar Receitas BOM</h5>
                        <p className="text-[10px] text-red-500 mt-0.5">Remove completamente todos os ingredientes/vínculos de todas as peças marcadas.</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleClearBulkRecipe}
                        disabled={selectedSkus.size === 0 || isSubmitting}
                        className="w-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2.5 rounded-lg shadow transition-colors disabled:opacity-50 uppercase tracking-wider"
                      >
                        Limpar BOM em Massa
                      </button>
                    </div>
                  </div>
                )}

             </div>
          )}
       </div>

    </div>
  );
}
